import { useApolloClient } from '@apollo/client';
import { Modal } from 'antd';
import PubSub from 'pubsub-js';
import { useEffect, useMemo, useRef, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { type CurrentUserDataFragment } from '../../../api/fragments/CurrentUserData';
import {
    type RefreshCredentialsMutation,
    type RefreshCredentialsMutationVariables,
    RefreshCredentialsDocument,
} from '../../../api/mutations/refreshCredentials';
import { useGetAuthenticatedUserLazyQuery } from '../../../api/queries/getAuthenticatedUser';
import { type Maybe } from '../../../api/types';
import { useThemeComponents } from '../../../themes';
import { sessionJWTStorage } from '../../../utilities/jwtStorage';
import validateTokenData from '../../../utilities/validateTokenData';
import LoadingElement from '../../LoadingElement';
import { AccountContext } from './Context';

export type AccountContextManagerProps = { children: JSX.Element | React.ReactNode };

type State = {
    token: string | null;
    data: Maybe<CurrentUserDataFragment>;
};

type SetTokenAction = { type: 'setToken'; token: string | null };
type SetDataAction = { type: 'setData'; data: Maybe<CurrentUserDataFragment> };
type SetSessionAction = {
    type: 'setSession';
    token: string;
    data: CurrentUserDataFragment;
};
type Action = SetTokenAction | SetDataAction | SetSessionAction;

const updateJWT = (token: string | null, rootMode: boolean) => {
    if (token) {
        // push the new token
        sessionJWTStorage.set(token, rootMode);
    } else {
        // remove the entry
        sessionJWTStorage.unset();
    }
};

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'setToken':
            // update the local storage
            // todo later use super user state to define either to use the root mode or not
            updateJWT(action.token, false);

            if (!action.token) {
                // reset
                return { token: null, data: null };
            }

            // simply replace the token
            return { ...state, token: action.token };

        case 'setData':
            return { ...state, data: action.data };

        case 'setSession':
            // update the state
            return { token: action.token, data: action.data };

        default:
            return state;
    }
};

const getInitialToken = () => {
    const token = sessionJWTStorage.get();

    if (!validateTokenData(token)) {
        if (token) {
            // if it's invalid, and there is token
            // we unset
            sessionJWTStorage.unset();
        }

        // invalid token
        return null;
    }

    return token;
};

export const AccountContextManager = ({ children }: AccountContextManagerProps) => {
    const { t } = useTranslation(['common']);
    const [state, dispatch] = useReducer(reducer, { token: getInitialToken(), data: null });
    const { token, data } = state;
    const { notification } = useThemeComponents();

    const actions = useMemo(
        () => ({
            setToken: (token: string | null) => dispatch({ type: 'setToken', token }),
            setData: (data: Maybe<CurrentUserDataFragment>) => dispatch({ type: 'setData', data }),
            setSession: (token: string, data: CurrentUserDataFragment) => {
                // because we need to update jwt token synchronously, we update here
                updateJWT(token, false);

                dispatch({ type: 'setSession', token, data });
            },
        }),
        [dispatch]
    );

    // user data fetch with the API
    const [getUser, { data: apiData, loading }] = useGetAuthenticatedUserLazyQuery({
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        // simply update our state to keep track of the data
        actions.setData(apiData?.currentUser || null);
    }, [apiData, actions]);

    useEffect(() => {
        // listen for any broadcast for invalid JWT
        const subscribeToken = PubSub.subscribe('core.unauthenticated', () => {
            // reset token
            actions.setToken(null);
            // display message
            notification.info({ content: t('common:messages.sessionTimeout'), key: 'primary' });
            // clear all modals (most likely those created with `Modal.method()`)
            Modal.destroyAll();
        });

        return () => {
            // unsubscribe cleanly
            PubSub.unsubscribe(subscribeToken);
        };
    }, [actions, notification, t]);

    // keep the session alive
    const client = useApolloClient();
    useEffect(() => {
        const tokenData = validateTokenData(token);

        if (!tokenData) {
            return () => undefined;
        }

        let mounted = true;

        const fn = async () => {
            if (!mounted) {
                // do nothing
                return;
            }

            const left = tokenData.exp - Date.now() / 1000;

            // renew 5mn ahead maximum
            if (left > 5 * 60) {
                // do nothing
                setTimeout(fn, 1000);

                // stop here
                return;
            }

            if (left <= 0) {
                // token is expired
                // reset token
                actions.setToken(null);

                // stop here
                return;
            }

            try {
                const response = await client.mutate<RefreshCredentialsMutation, RefreshCredentialsMutationVariables>({
                    mutation: RefreshCredentialsDocument,
                });

                if (mounted) {
                    actions.setToken(response?.data?.refreshCredentials || null);
                }
            } catch (error) {
                // print it out
                console.info('Failed to renew the session');
                console.error(error);
            }
        };

        setTimeout(fn, 200);

        return () => {
            mounted = false;
        };
    }, [token, actions, client]);

    // computed the contexts
    const context = useMemo(
        () => ({
            token,
            data,
            refetch: getUser,
            login: actions.setSession,
            logout: () => actions.setToken(null),
        }),
        [data, getUser, actions, token]
    );

    // refetch data whenever the token changed
    const tokenRef = useRef(token);
    useEffect(() => {
        if (tokenRef.current !== token) {
            // first update the local storage
            actions.setToken(token);

            // then update reference
            tokenRef.current = token;

            // refetch data
            getUser();

            // inform about it
            PubSub.publish('core.tokenUpdate', { token });
        }
    }, [getUser, token, tokenRef, actions]);

    // on mount, we get the user
    const hasMountRef = useRef(false);
    useEffect(() => {
        if (!hasMountRef.current) {
            hasMountRef.current = true;
            // if token is not expired, we call get user
            if (token) {
                getUser();
            }
        }
    }, [getUser, token]);

    const isLoading =
        (loading && token && !data) || (!loading && apiData?.currentUser && !data) || !hasMountRef.current;

    if (isLoading) {
        return <LoadingElement />;
    }

    return <AccountContext.Provider value={context}>{children}</AccountContext.Provider>;
};
